/**
 * @file   src\Pages\ManageCampaigns.tsx
 * @brief  Campaigns Listing page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, useState, Container, Table,  Button,  Pagination, useEffect, useOutletContext, useNavigate, useParams, Modal } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import EditIcon from '../assets/images/icons/editicon';
import DeleteIcon from '../assets/images/icons/deleteicon';
import AddIcon from '../assets/images/icons/addicon';
import { userType } from '../utils/enums';
import { AppContextType } from '../interfaces/generalInterface';
import { CampaignItem } from '../interfaces/promotionInterface';
import { pageURLs } from '../utils/pageUrls';
import { CampaignApi, CompanyApi } from '../utils/apiUrls';
import axios from '../config/axiosConfig';
import SmallLoader from '../components/SmallLoader';
import { MessageToaster } from '../utils/toastUtil';
import moment from "moment-timezone"

const ManageCampaigns = (props: any) => {

    const carwash_owner = (props.userType === userType['CARWASH-OWNER']) ? true : false;
    const navigate = useNavigate()


    // State to company details
    const [company, setCompany] = useState({
        companyName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipCode: ""
    });

    const toaster = new MessageToaster();

     // Access the context
     const { updateData } = useOutletContext<AppContextType>();

    const [totalPages, setTotalPages] = useState(1);

    const param = useParams();

    const [loading, setLoading] = useState(true);

   
    // State to handle listed campaigns
    const [campaigns, setCampaigns] = useState<CampaignItem[]>([])

    const [currentPage, setCurrentPage] = useState(1);
    const [selected_campaign_for_delete, setSelected_campaign_for_delete] = useState<any>(0);
    const [selected_campaign_name_for_delete, setSelected_campaign_name_for_delete] = useState<any>('');
    const [selected_campaign_location_for_delete, setSelected_campaign_location_for_delete] = useState<any>(0);
    const [showCampaignDeletepoup, setCampaignDeletepoup] = useState<boolean>(false);

    useEffect(() => {
        getCompanyAddress()
        getCompany()
        getCampaigns(currentPage)
    }, [])
    useEffect(() => {
        handleUpdate()
    }, [company])

    // Update the data
    const handleUpdate = async () => {
        await updateData({ heading:  "Campaigns", url: "", showBackButton:  false, showMainMenu: true, activeLink:  "" });
    };


    // To get company details
    const getCompanyAddress = async () => {
        try {
            const id = param.id ? parseInt(param.id) : 0;
            const { data } = (!carwash_owner) ? await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id) : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS) ;
            if (data?.error === 0) {
                setCompany(data.data);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    // Function to fetch company information
    const getCompany = async () => {
        try {
            const param1 = `${param.id}`;
            if(!carwash_owner)
            {
                await axios.get(CompanyApi.GET_COMPANY + param1)
            }
           else{
                await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS);
           }

        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };


    // Get list of campaigns
    const getCampaigns = async (pageNo = 1) => {
        try {
            const { data } = await axios.get(CampaignApi.LIST_CAMPAIGNS +`?pageNo=${pageNo}`);
            if (data?.error === 0 && Array.isArray(data?.data?.list)) {
                const campaign_list: any[] = data.data.list;
                if (campaign_list.length > 0) {
                    // Transform the array to match the structure of the Location interface
                    const transformedArray:any = campaign_list.map(item => ({
                        assignedlocation: item.locationName,
                        washname: item.wshName,
                        campaignname: item.campaignName,
                        generatecode: item.campaignCode,
                        discounttype: item.discountType,
                        discount_value: item.discountValue,
                        validFrom: item.validFrom,
                        validTo: item.validTo,
                        campaignId: item.campaignID,
                        locationId: item.locationId,
                        HasMultipleCode: item.HasMultipleCode,
                        IsCompleted:item.IsCompleted,
                        ReportURL:item.ReportURL,
                        
                    }));
                    setCampaigns(transformedArray)
                    setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);
                } else {
                    console.log('Campaign list is empty');
                    setCampaigns([]);  // Set to an empty array if campaign_list is empty
                }
            } else {
                console.log('Campaign list is not available or is not an array');
            }
            setLoading(false)
        } catch (error: any) {
            console.error('Error fetching company list:', error.message);
            setLoading(false)
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const getVisiblePageNumbers = () => {
        const totalPageNumbersToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(totalPageNumbersToShow / 2));
        let endPage = startPage + totalPageNumbersToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return pageNumbers;
    };

  
    const deleteCampaign = async () => {
        try {
            const campaignId = selected_campaign_for_delete;
            const locationId = selected_campaign_location_for_delete;
            const { data } =  await axios.post(CampaignApi.DELETE_CAMPAIGN, { locationId: locationId, campaignId: campaignId })
           

            if (data?.error === 0) {
                // Reload campaign list
                hideshowCampaignDeleteModal()
                getCampaigns(currentPage)
            }
            else {
                // show error message
                hideshowCampaignDeleteModal()
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        } finally {
            setLoading(false)
        }
    }


    // To format date
    const formatDate = (createdDate: string) => {
        const date = moment(createdDate);
        const formattedDate = date.format('MM/DD/YYYY');
        return formattedDate
    }

    
    //Hide ppopup
    const hideshowCampaignDeleteModal = () => {
        setCampaignDeletepoup(false);
    }


    const DeletebuttonClick = (delete_campaign_id: number | undefined, delete_location_id: number | undefined, delete_campaign_name: string | undefined) => {
        setSelected_campaign_for_delete(delete_campaign_id);
        setSelected_campaign_location_for_delete(delete_location_id);
        setSelected_campaign_name_for_delete(delete_campaign_name);
        setCampaignDeletepoup(true);
    }

    //genererated codes download function 
    const handleDownload = (excelFileName:string) => {
        const fileUrl = `${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${excelFileName}`;
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = excelFileName; 
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };


    return (

        <div className='manage-carwash-container manage-subscription-container'>
            <Container>
                
                   <div className="manage-header-block d-flex justify-content-end">
                    <Button
                        title="Add New Campaign"
                        type="submit"
                        variant="primary"
                        className="primary-btn ms-auto d-flex align-items-center"
                        onClick={() => navigate(`/${pageURLs.add_campaign}`)}
                    >
                        Add New Campaign
                        <AddIcon />
                    </Button>
                </div>

                <div className="cw-table-wrap">
                    <Table responsive className='m-0'>
                        <thead>
                            <tr>
                                <th>Assigned Location</th>
                                <th>Assigned Wash Name</th>
                                <th>Campaign Name</th>
                                <th>Campaign Code</th>
                                <th>Discount Type</th>
                                <th>Value ($ or %)</th>
                                <th>Valid From</th>
                                <th>Valid To</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {campaigns.length ?
                                (campaigns.map((item, index) =>
                                (
                                    <tr key={index}>
                                        <td>{item.assignedlocation}</td>
                                        <td>{item.washname}</td>
                                        <td>{item.campaignname}</td>
                                        <td>{item.HasMultipleCode ? (item.IsCompleted ? <Button variant="light" className="light-btn action-btn" onClick={() => handleDownload(item.ReportURL?item.ReportURL:"")}>
      Download
    </Button>: <span className='inprogress'>Code generation In Progress</span>) :item.generatecode}</td>
                                        <td>{item.discounttype === 1 ? "Amount" : "Percentage"}</td>
                                        <td>{item.discounttype === 1 ? "$" : null} {item.discount_value}{item.discounttype === 1 ? null : "%"}</td>
                                        <td>{formatDate(item.validFrom)}</td>
                                        <td>{formatDate(item.validTo)}</td>
                                        <td className='nowrap'>
                                            <div className="active-group d-flex align-items-center">
                                                <Button variant="light" className="light-btn action-btn" title="Edit" onClick={() => navigate(`/${pageURLs.edit_campaign}/${item.campaignId}`)}><EditIcon /></Button>
                                                <Button variant="light" className="light-btn action-btn" title="Delete" onClick={() => DeletebuttonClick(item.campaignId, item.locationId, item.campaignname)}><DeleteIcon /></Button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                                )
                                ) :
                                (
                                    <tr>
                                        <td style={{ textAlign: 'center' }} colSpan={9} >No data found.</td>
                                    </tr>
                                )}

                        </tbody>
                    </Table>
                    {campaigns.length ? <div className="mt-3 d-flex justify-content-end">
                        <Pagination>
                            <Pagination.First
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(1)}
                            />
                            <Pagination.Prev
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            />
                            {getVisiblePageNumbers().map((pageNumber) => (
                                <Pagination.Item
                                    key={pageNumber}
                                    active={pageNumber === currentPage}
                                    onClick={() => handlePageChange(pageNumber)}
                                >
                                    {pageNumber}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            />
                            <Pagination.Last
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(totalPages)}
                            />
                        </Pagination>
                    </div> : null}
       
                </div>

            </Container>
            {loading && <SmallLoader />}

<Modal
        show={showCampaignDeletepoup}
        onHide={hideshowCampaignDeleteModal}
        centered
        className='add-modal'
    >
        <Modal.Body className='text-center'>
            <h5 className='f-bold text-center'>Do you want to delete the campaign {selected_campaign_name_for_delete}?</h5>
            <div className="btn-sec d-grid">
                <Button variant="light" className="secondary-btn" onClick={hideshowCampaignDeleteModal}>Not Now</Button>
                <Button variant="primary" className='primary-btn btn-main m-0' onClick={deleteCampaign}>Yes</Button>
            </div>
        </Modal.Body>
    </Modal>

        </div>
    )
}

export default ManageCampaigns;
